import React, { useEffect, useRef, useState } from "react";

const Card = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.12,
      }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const philosophySteps = [
    "Passion",
    "Education",
    "Skills",
    "Leadership",
    "Opportunity",
    "A Better Future",
  ];

  return (
    <>
      <style>{`
        /* =========================================================
           PATIF — INSTITUTIONAL FOCUS
           PASSION AIDS THE FUTURE
        ========================================================= */

        .patif-section {
          position: relative;
          width: 100%;
          overflow: hidden;
          padding: 110px 20px 120px;
          background:
            radial-gradient(
              circle at 85% 12%,
              rgba(174, 255, 0, 0.10),
              transparent 28%
            ),
            radial-gradient(
              circle at 8% 82%,
              rgba(126, 34, 206, 0.13),
              transparent 30%
            ),
            linear-gradient(
              145deg,
              #09050f 0%,
              #13091d 42%,
              #08090a 100%
            );
          font-family:
            "Poppins",
            "Segoe UI",
            Arial,
            sans-serif;
          color: #ffffff;
        }

        /* =========================================================
           BACKGROUND
        ========================================================= */

        .patif-bg-orb {
          position: absolute;
          width: 500px;
          height: 500px;
          border-radius: 50%;
          background:
            radial-gradient(
              circle,
              rgba(190, 255, 0, 0.08),
              transparent 68%
            );
          top: -260px;
          right: -180px;
          pointer-events: none;
        }

        .patif-bg-orb-two {
          position: absolute;
          width: 430px;
          height: 430px;
          border-radius: 50%;
          background:
            radial-gradient(
              circle,
              rgba(168, 85, 247, 0.10),
              transparent 68%
            );
          bottom: -230px;
          left: -180px;
          pointer-events: none;
        }

        .patif-grid-pattern {
          position: absolute;
          inset: 0;
          opacity: 0.055;
          pointer-events: none;
          background-image:
            linear-gradient(
              rgba(255, 255, 255, 0.25) 1px,
              transparent 1px
            ),
            linear-gradient(
              90deg,
              rgba(255, 255, 255, 0.25) 1px,
              transparent 1px
            );
          background-size: 55px 55px;
          mask-image:
            linear-gradient(
              to bottom,
              black,
              transparent 85%
            );
        }

        .patif-container {
          position: relative;
          z-index: 2;
          width: min(1240px, 100%);
          margin: 0 auto;
        }

        /* =========================================================
           TOP HEADER
        ========================================================= */

        .patif-header {
          max-width: 980px;
          margin: 0 auto 70px;
          text-align: center;
          opacity: 0;
          transform: translateY(35px);
          transition:
            opacity 0.8s ease,
            transform 0.8s
              cubic-bezier(0.22, 1, 0.36, 1);
        }

        .patif-header.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        .patif-label {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 9px 17px;
          margin-bottom: 22px;
          border: 1px solid
            rgba(194, 255, 0, 0.30);
          border-radius: 100px;
          background:
            rgba(194, 255, 0, 0.08);
          color: #d6ff45;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 2px;
          text-transform: uppercase;
        }

        .patif-label::before {
          content: "";
          width: 8px;
          height: 8px;
          flex-shrink: 0;
          border-radius: 50%;
          background: #d6ff45;
          box-shadow:
            0 0 0 5px
            rgba(214, 255, 69, 0.10);
        }

        .patif-heading {
          margin: 0;
          font-size:
            clamp(38px, 6vw, 72px);
          line-height: 0.98;
          font-weight: 900;
          letter-spacing: -3px;
          color: #ffffff;
        }

        .patif-heading-highlight {
          display: block;
          margin-top: 12px;
          background:
            linear-gradient(
              90deg,
              #d6ff45 0%,
              #b8ff00 35%,
              #f5d63d 72%,
              #ffffff 100%
            );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .patif-header-description {
          max-width: 790px;
          margin: 27px auto 0;
          color: #b8afbF;
          font-size: 14px;
          line-height: 1.9;
        }

        /* =========================================================
           MAIN FEATURE PANEL
        ========================================================= */

        .patif-feature {
          position: relative;
          display: grid;
          grid-template-columns:
            minmax(0, 0.95fr)
            minmax(0, 1.45fr);
          gap: 0;
          overflow: hidden;
          border: 1px solid
            rgba(255, 255, 255, 0.10);
          border-radius: 30px;
          background:
            linear-gradient(
              135deg,
              rgba(255, 255, 255, 0.075),
              rgba(255, 255, 255, 0.025)
            );
          box-shadow:
            0 35px 100px
            rgba(0, 0, 0, 0.42);
          backdrop-filter: blur(18px);
          opacity: 0;
          transform:
            translateY(60px)
            scale(0.98);
          transition:
            opacity 0.9s ease 0.15s,
            transform 0.9s
              cubic-bezier(0.22, 1, 0.36, 1) 0.15s;
        }

        .patif-feature.is-visible {
          opacity: 1;
          transform:
            translateY(0)
            scale(1);
        }

        /* =========================================================
           LEFT SIDE
        ========================================================= */

        .patif-focus-panel {
          position: relative;
          min-height: 540px;
          padding: 48px 43px;
          overflow: hidden;
          background:
            linear-gradient(
              155deg,
              #5815a4 0%,
              #32105f 52%,
              #170b26 100%
            );
        }

        .patif-focus-panel::before {
          content: "";
          position: absolute;
          width: 260px;
          height: 260px;
          right: -100px;
          top: -100px;
          border-radius: 50%;
          background:
            rgba(211, 255, 60, 0.12);
        }

        .patif-focus-panel::after {
          content: "";
          position: absolute;
          width: 190px;
          height: 190px;
          left: -90px;
          bottom: -100px;
          border-radius: 50%;
          border: 1px solid
            rgba(255, 255, 255, 0.10);
        }

        .patif-focus-content {
          position: relative;
          z-index: 2;
        }

        .patif-small-label {
          display: inline-block;
          margin-bottom: 28px;
          color: #e4ff64;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 1.8px;
          text-transform: uppercase;
        }

        .patif-focus-title {
          margin: 0;
          max-width: 500px;
          color: #ffffff;
          font-size:
            clamp(31px, 4vw, 49px);
          font-weight: 900;
          line-height: 1.05;
          letter-spacing: -1.8px;
        }

        .patif-focus-title span {
          color: #d6ff45;
        }

        .patif-focus-text {
          max-width: 500px;
          margin: 25px 0 0;
          color: rgba(255, 255, 255, 0.74);
          font-size: 13.5px;
          line-height: 1.85;
        }

        .patif-focus-list {
          position: relative;
          z-index: 2;
          display: grid;
          gap: 13px;
          margin: 35px 0 0;
          padding: 0;
          list-style: none;
        }

        .patif-focus-item {
          display: flex;
          align-items: flex-start;
          gap: 13px;
          color: #ffffff;
          font-size: 12.5px;
          line-height: 1.55;
        }

        .patif-check {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          flex-shrink: 0;
          border-radius: 8px;
          background: #d6ff45;
          color: #17101e;
          font-size: 12px;
          font-weight: 900;
        }

        /* =========================================================
           RIGHT SIDE
        ========================================================= */

        .patif-information {
          padding: 48px;
          background:
            rgba(5, 5, 7, 0.60);
        }

        .patif-info-grid {
          display: grid;
          grid-template-columns:
            repeat(2, minmax(0, 1fr));
          gap: 20px;
        }

        .patif-info-card {
          position: relative;
          overflow: hidden;
          min-height: 225px;
          padding: 29px;
          border: 1px solid
            rgba(255, 255, 255, 0.085);
          border-radius: 21px;
          background:
            rgba(255, 255, 255, 0.035);
          transition:
            transform 0.35s ease,
            border-color 0.35s ease,
            background 0.35s ease;
        }

        .patif-info-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 65px;
          height: 4px;
          border-radius:
            0 0 10px 0;
          background: #d6ff45;
          transition: width 0.35s ease;
        }

        .patif-info-card:nth-child(2)::before {
          background: #f4ce32;
        }

        .patif-info-card:nth-child(3)::before {
          background: #7c3aed;
        }

        .patif-info-card:nth-child(4)::before {
          background: #34d399;
        }

        .patif-info-card:hover {
          transform: translateY(-5px);
          border-color:
            rgba(214, 255, 69, 0.28);
          background:
            rgba(255, 255, 255, 0.055);
        }

        .patif-info-card:hover::before {
          width: 110px;
        }

        .patif-card-number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 35px;
          height: 35px;
          margin-bottom: 19px;
          border-radius: 11px;
          background:
            rgba(214, 255, 69, 0.11);
          color: #d6ff45;
          font-size: 10px;
          font-weight: 900;
        }

        .patif-info-card:nth-child(2)
          .patif-card-number {
          color: #f4ce32;
          background:
            rgba(244, 206, 50, 0.10);
        }

        .patif-info-card:nth-child(3)
          .patif-card-number {
          color: #b784ff;
          background:
            rgba(124, 58, 237, 0.13);
        }

        .patif-info-card:nth-child(4)
          .patif-card-number {
          color: #53e3a8;
          background:
            rgba(52, 211, 153, 0.10);
        }

        .patif-info-title {
          margin: 0 0 12px;
          color: #ffffff;
          font-size: 19px;
          font-weight: 800;
          line-height: 1.2;
        }

        .patif-info-text {
          margin: 0;
          color: #aaa1b2;
          font-size: 12.5px;
          line-height: 1.75;
        }

        /* =========================================================
           PHILOSOPHY STRIP
        ========================================================= */

        .patif-philosophy {
          margin-top: 30px;
          padding: 31px 35px;
          border:
            1px solid
            rgba(255, 255, 255, 0.09);
          border-radius: 24px;
          background:
            linear-gradient(
              90deg,
              rgba(214, 255, 69, 0.08),
              rgba(124, 58, 237, 0.07),
              rgba(255, 255, 255, 0.025)
            );
        }

        .patif-philosophy-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 25px;
        }

        .patif-philosophy-title {
          margin: 0;
          color: #ffffff;
          font-size: 16px;
          font-weight: 800;
        }

        .patif-philosophy-caption {
          color: #8f8798;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .patif-philosophy-flow {
          display: grid;
          grid-template-columns:
            repeat(6, minmax(0, 1fr));
          align-items: center;
        }

        .patif-step {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 52px;
          padding: 10px 12px;
          text-align: center;
          color: #ffffff;
          font-size: 10px;
          font-weight: 800;
          line-height: 1.3;
        }

        .patif-step:not(:last-child)::after {
          content: "→";
          position: absolute;
          right: -7px;
          color: #d6ff45;
          font-size: 15px;
          font-weight: 900;
        }

        .patif-step:first-child {
          color: #d6ff45;
        }

        .patif-step:nth-child(2) {
          color: #ffffff;
        }

        .patif-step:nth-child(3) {
          color: #d7a7ff;
        }

        .patif-step:nth-child(4) {
          color: #64e7b1;
        }

        .patif-step:nth-child(5) {
          color: #f6d856;
        }

        .patif-step:nth-child(6) {
          color: #ffffff;
        }

        /* =========================================================
           BOTTOM BRAND STATEMENT
        ========================================================= */

        .patif-bottom-message {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 30px;
          margin-top: 42px;
          padding: 0 10px;
        }

        .patif-bottom-title {
          margin: 0;
          color: #ffffff;
          font-size: 19px;
          font-weight: 800;
        }

        .patif-bottom-title span {
          color: #d6ff45;
        }

        .patif-bottom-text {
          max-width: 570px;
          margin: 0;
          color: #938a99;
          font-size: 12px;
          line-height: 1.7;
          text-align: right;
        }

        /* =========================================================
           TABLET
        ========================================================= */

        @media (max-width: 1050px) {

          .patif-section {
            padding:
              90px 18px 100px;
          }

          .patif-feature {
            grid-template-columns:
              1fr;
          }

          .patif-focus-panel {
            min-height: auto;
          }

          .patif-info-grid {
            gap: 16px;
          }

          .patif-philosophy-flow {
            grid-template-columns:
              repeat(3, 1fr);
            row-gap: 10px;
          }

          .patif-step:not(:last-child)::after {
            display: none;
          }
        }

        /* =========================================================
           MOBILE
        ========================================================= */

        @media (max-width: 700px) {

          .patif-section {
            padding:
              75px 14px 80px;
          }

          .patif-header {
            margin-bottom: 42px;
          }

          .patif-heading {
            font-size:
              clamp(34px, 10vw, 48px);
            letter-spacing: -1.8px;
          }

          .patif-header-description {
            margin-top: 21px;
            font-size: 12.5px;
            line-height: 1.8;
          }

          .patif-feature {
            border-radius: 22px;
          }

          .patif-focus-panel {
            padding: 35px 25px 38px;
          }

          .patif-focus-title {
            font-size: 31px;
            letter-spacing: -1px;
          }

          .patif-focus-text {
            font-size: 12.5px;
          }

          .patif-information {
            padding: 22px;
          }

          .patif-info-grid {
            grid-template-columns:
              1fr;
          }

          .patif-info-card {
            min-height: auto;
            padding: 25px;
          }

          .patif-philosophy {
            padding: 25px 20px;
          }

          .patif-philosophy-heading {
            display: block;
          }

          .patif-philosophy-caption {
            display: block;
            margin-top: 7px;
          }

          .patif-philosophy-flow {
            grid-template-columns:
              repeat(2, 1fr);
            gap: 8px;
          }

          .patif-step {
            min-height: 48px;
            border:
              1px solid
              rgba(255, 255, 255, 0.06);
            border-radius: 10px;
            background:
              rgba(255, 255, 255, 0.025);
          }

          .patif-bottom-message {
            display: block;
            margin-top: 30px;
          }

          .patif-bottom-text {
            margin-top: 9px;
            text-align: left;
          }
        }

        /* =========================================================
           SMALL MOBILE
        ========================================================= */

        @media (max-width: 420px) {

          .patif-section {
            padding:
              62px 12px 70px;
          }

          .patif-label {
            padding:
              8px 13px;
            font-size:
              8px;
            letter-spacing:
              1.4px;
          }

          .patif-heading {
            font-size: 31px;
          }

          .patif-focus-panel {
            padding:
              29px 21px 32px;
          }

          .patif-focus-title {
            font-size: 27px;
          }

          .patif-information {
            padding: 16px;
          }

          .patif-info-card {
            padding: 21px;
          }

          .patif-info-title {
            font-size: 17px;
          }

          .patif-info-text {
            font-size: 12px;
          }

          .patif-philosophy {
            padding:
              21px 15px;
          }

          .patif-philosophy-flow {
            grid-template-columns:
              1fr 1fr;
          }

          .patif-bottom-title {
            font-size: 17px;
          }
        }

        /* =========================================================
           REDUCED MOTION
        ========================================================= */

        @media (prefers-reduced-motion: reduce) {

          .patif-header,
          .patif-feature,
          .patif-info-card {
            transition: none !important;
          }
        }
      `}</style>

      <section
        ref={sectionRef}
        className="patif-section"
      >
        <div className="patif-bg-orb" />
        <div className="patif-bg-orb-two" />
        <div className="patif-grid-pattern" />

        <div className="patif-container">

          {/* =====================================================
              HEADER
          ===================================================== */}

          <header
            className={`patif-header ${
              visible ? "is-visible" : ""
            }`}
          >
            <span className="patif-label">
              PASSION AIDS THE FUTURE
            </span>

            <h2 className="patif-heading">
              Our Institutional
              <span className="patif-heading-highlight">
                Focus & Purpose
              </span>
            </h2>

            <p className="patif-header-description">
              PASSION AIDS THE FUTURE is committed to accessible,
              practical, and professional education that transforms
              passion into knowledge, knowledge into skills, and
              skills into meaningful opportunities for individuals,
              families, workplaces, and communities.
            </p>
          </header>

          {/* =====================================================
              MAIN FEATURE
          ===================================================== */}

          <div
            className={`patif-feature ${
              visible ? "is-visible" : ""
            }`}
          >

            {/* ===================================================
                INSTITUTIONAL FOCUS
            =================================================== */}

            <div className="patif-focus-panel">

              <div className="patif-focus-content">

                <span className="patif-small-label">
                  What We Stand For
                </span>

                <h3 className="patif-focus-title">
                  Building People.
                  <br />
                  Creating <span>Opportunity.</span>
                </h3>

                <p className="patif-focus-text">
                  PASSION AIDS THE FUTURE is designed to provide
                  accessible, practical and professional education
                  that empowers individuals with knowledge, skills,
                  leadership capacity and professional competencies
                  needed to succeed in the workplace and contribute
                  meaningfully to their communities.
                </p>

                <ul className="patif-focus-list">

                  <li className="patif-focus-item">
                    <span className="patif-check">✓</span>
                    <span>
                      Professional education and development
                    </span>
                  </li>

                  <li className="patif-focus-item">
                    <span className="patif-check">✓</span>
                    <span>
                      Vocational and practical skills
                    </span>
                  </li>

                  <li className="patif-focus-item">
                    <span className="patif-check">✓</span>
                    <span>
                      Leadership and entrepreneurship
                    </span>
                  </li>

                  <li className="patif-focus-item">
                    <span className="patif-check">✓</span>
                    <span>
                      Compliance, justice and community empowerment
                    </span>
                  </li>

                </ul>

              </div>

            </div>

            {/* ===================================================
                INFORMATION CARDS
            =================================================== */}

            <div className="patif-information">

              <div className="patif-info-grid">

                <article className="patif-info-card">

                  <div className="patif-card-number">
                    01
                  </div>

                  <h3 className="patif-info-title">
                    Our Philosophy
                  </h3>

                  <p className="patif-info-text">
                    We believe transformation begins with passion.
                    Through education, practical skills and
                    leadership development, individuals can create
                    opportunities and contribute to a better future.
                  </p>

                </article>

                <article className="patif-info-card">

                  <div className="patif-card-number">
                    02
                  </div>

                  <h3 className="patif-info-title">
                    Our Vision
                  </h3>

                  <p className="patif-info-text">
                    To become a leading institution for professional
                    education, vocational training, leadership
                    development, entrepreneurship and community
                    empowerment.
                  </p>

                </article>

                <article className="patif-info-card">

                  <div className="patif-card-number">
                    03
                  </div>

                  <h3 className="patif-info-title">
                    Our Mission
                  </h3>

                  <p className="patif-info-text">
                    To provide accessible, inclusive and quality
                    professional and vocational education that
                    equips individuals with knowledge, practical
                    skills, ethical values and leadership capacity.
                  </p>

                </article>

                <article className="patif-info-card">

                  <div className="patif-card-number">
                    04
                  </div>

                  <h3 className="patif-info-title">
                    Our Commitment
                  </h3>

                  <p className="patif-info-text">
                    We seek to help people succeed in their careers,
                    create opportunities and contribute to the
                    sustainable development of their communities
                    and society.
                  </p>

                </article>

              </div>

              {/* =================================================
                  PHILOSOPHY FLOW
              ================================================= */}

              <div className="patif-philosophy">

                <div className="patif-philosophy-heading">

                  <h3 className="patif-philosophy-title">
                    The PATIF Development Path
                  </h3>

                  <span className="patif-philosophy-caption">
                    From passion to impact
                  </span>

                </div>

                <div className="patif-philosophy-flow">

                  {philosophySteps.map(
                    (step, index) => (
                      <div
                        key={step}
                        className="patif-step"
                      >
                        {step}
                      </div>
                    )
                  )}

                </div>

              </div>

            </div>

          </div>

          {/* =====================================================
              BOTTOM STATEMENT
          ===================================================== */}

          <div className="patif-bottom-message">

            <h3 className="patif-bottom-title">
              Passion is the beginning.
              <span> Opportunity is the destination.</span>
            </h3>

            <p className="patif-bottom-text">
              Through the College of Professional and Vocational
              Studies, PATIF seeks to transform passion into
              knowledge, knowledge into practical skills, skills
              into professional opportunities, and opportunities
              into sustainable community development.
            </p>

          </div>

        </div>
      </section>
    </>
  );
};

export default Card;

